import { AlertTriangle, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, Field, FormGrid, ModalError } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { ApiError } from '@/lib/api';
import { JEDNOSTKI_OKNA, type Okno } from '@/lib/permissions-api';

/**
 * OKNO LIMITU — wspólny formularz dla „Ustaw limit" i „Zmień limit".
 *
 * ⚠️ JEDNOSTKA JEST OBOWIĄZKOWA I WIDOCZNA. Sama liczba nic nie znaczy: „30" na regule godzinowej
 * to trzydzieści godzin, a na tygodniowej trzydzieści tygodni. Poprzednia wersja modułu Limitów
 * znała tylko dni i liczyła okna 720× za szerokie.
 */
export function OknoLimituModal({
  tytul,
  opis,
  poczatkowe,
  onClose,
  onZapisz,
}: {
  tytul: string;
  opis: string;
  poczatkowe: Okno | null;
  onClose: () => void;
  onZapisz: (o: Okno) => Promise<void>;
}) {
  const [maxQty, setMaxQty] = useState(String(poczatkowe?.maxQty ?? 1));
  const [period, setPeriod] = useState(String(poczatkowe?.period ?? 1));
  const [periodType, setPeriodType] = useState(String(poczatkowe?.periodType ?? 3));
  const [blad, setBlad] = useState<string | null>(null);
  const [zapisuje, setZapisuje] = useState(false);

  const liczby = { maxQty: Number(maxQty), period: Number(period), periodType: Number(periodType) };
  const poprawne =
    Number.isInteger(liczby.maxQty) && liczby.maxQty >= 0 && Number.isFinite(liczby.period) && liczby.period > 0;
  const nieEgzekwowane = liczby.periodType === 5 || liczby.periodType === 6;

  const zapisz = async () => {
    setBlad(null);
    setZapisuje(true);
    try {
      await onZapisz({ maxQty: liczby.maxQty, period: liczby.period, periodType: liczby.periodType });
      onClose();
    } catch (e) {
      setBlad(e instanceof ApiError ? e.message : 'Nie udało się zapisać');
    } finally {
      setZapisuje(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent title={tytul} description={opis}>
        <FormGrid>
          <Field label="Ile sztuk" htmlFor="maxQty" required>
            <Input
              id="maxQty"
              type="number"
              min={0}
              autoFocus
              value={maxQty}
              onChange={(e) => setMaxQty(e.target.value)}
            />
          </Field>
          <Field label="W czasie" htmlFor="period" required>
            <div className="flex gap-2">
              <Input
                id="period"
                type="number"
                min={0}
                step="any"
                className="w-24"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
              />
              <Select value={periodType} onChange={(e) => setPeriodType(e.target.value)} className="flex-1">
                {JEDNOSTKI_OKNA.map((j) => (
                  <option key={j.v} value={j.v}>
                    {j.l}
                  </option>
                ))}
              </Select>
            </div>
          </Field>
        </FormGrid>

        {/* ⚠️ SPSUI egzekwuje tylko okresy 1-4. Reguła miesięczna albo roczna zapisze się
            poprawnie i NIC nie zrobi — trzeba to powiedzieć przed zapisem, nie po. */}
        {nieEgzekwowane && (
          <p className="mt-2 flex items-start gap-1.5 rounded border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-2 text-xs">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--warning)]" />
            <span>
              Automat NIE egzekwuje okresów miesięcznych ani rocznych — taki limit zapisze się, ale nikt go nie będzie
              pilnował. Jeśli ma działać, wybierz dni albo tygodnie.
            </span>
          </p>
        )}
        <p className="mt-2 text-xs text-muted-foreground">
          Czas liczy się KROCZĄCO — wstecz od chwili wydania, nie od początku miesiąca.
        </p>

        {blad && <ModalError>{blad}</ModalError>}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={zapisuje}>
            Anuluj
          </Button>
          <Button type="button" onClick={() => void zapisz()} disabled={!poprawne || zapisuje}>
            {zapisuje && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Zapisz
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
